import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export interface AppMenuItem {
  label: string;
  icon?: ReactNode;
  /** Short trailing detail, such as the current plan. */
  meta?: string;
  tone?: 'danger';
  onSelect: () => void;
}

interface AppMenuProps {
  anchor: HTMLElement;
  items: AppMenuItem[];
  placement?: 'below-end' | 'above-stretch';
  onClose: () => void;
}

const MENU_GAP_PX = 6;

/** Small popover anchored to an element. Fixed positioning keeps it clear of scrolling containers. */
function AppMenu({ anchor, items, placement = 'below-end', onClose }: AppMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' });

  useLayoutEffect(() => {
    const rect = anchor.getBoundingClientRect();

    setStyle(placement === 'above-stretch'
      ? { left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + MENU_GAP_PX }
      : { right: window.innerWidth - rect.right, top: rect.bottom + MENU_GAP_PX });
    menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [anchor, placement]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;

      if (!menuRef.current?.contains(target) && !anchor.contains(target)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Handled here, so views listening for Escape (like the issue panel) stay open.
        event.preventDefault();
        onClose();
        anchor.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', onClose);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', onClose);
    };
  }, [anchor, onClose]);

  return (
    <div ref={menuRef} className="app-menu" role="menu" style={style}>
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          role="menuitem"
          className={`app-menu-item${item.tone === 'danger' ? ' danger' : ''}`}
          onClick={() => {
            onClose();
            item.onSelect();
          }}
        >
          {item.icon}
          <span>{item.label}</span>
          {item.meta && <span className="app-menu-item-meta">{item.meta}</span>}
        </button>
      ))}
    </div>
  );
}

export default AppMenu;
